import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalculatorIcon, TrendingUpIcon, AwardIcon } from 'lucide-react';
interface CareerLevel {
  name: string;
  emoji: string;
  description: string;
  color: string;
  bgGradient: string;
  progress: number;
}
function getCareerLevel(rentals: number, sales: number): CareerLevel {
  if (rentals === 0 && sales === 0) {
    return {
      name: 'Trainee',
      emoji: '🎓',
      description: 'Etapa de formare – parcurgi trainingurile și onboardingul.',
      color: 'text-gray-700',
      bgGradient: 'from-gray-100 to-gray-200',
      progress: 0
    };
  }
  if (rentals >= 3 && sales >= 10) {
    return {
      name: 'Selling Master',
      emoji: '👑',
      description: 'Nivel de top – devii mentor și poți coordona alți agenți!',
      color: 'text-green-900',
      bgGradient: 'from-green-400 to-green-500',
      progress: 100
    };
  }
  if (rentals >= 3 && sales >= 9) {
    return {
      name: 'Selling Senior',
      emoji: '⭐',
      description: 'Nivel avansat – agent stabil cu portofoliu consistent.',
      color: 'text-green-800',
      bgGradient: 'from-green-300 to-green-400',
      progress: 85
    };
  }
  if (rentals >= 3 && sales >= 4) {
    return {
      name: 'Selling Broker',
      emoji: '💼',
      description: 'Lucrezi cu portofolii complexe și primești leaduri premium.',
      color: 'text-green-700',
      bgGradient: 'from-green-200 to-green-300',
      progress: 70
    };
  }
  if (rentals >= 3 && sales >= 1) {
    return {
      name: 'Selling Closer',
      emoji: '🎯',
      description: 'Gestionezi atât cumpărători, cât și proprietari pe vânzări.',
      color: 'text-green-600',
      bgGradient: 'from-green-100 to-green-200',
      progress: 50
    };
  }
  if (rentals >= 3) {
    return {
      name: 'Selling Closer',
      emoji: '🎯',
      description: 'Cu 3 închirieri finalizate, ai acces la listele de proprietari!',
      color: 'text-green-600',
      bgGradient: 'from-green-100 to-green-200',
      progress: 50
    };
  }
  if (rentals >= 1) {
    return {
      name: 'Rental Buyer',
      emoji: '🔑',
      description: 'Ai acces la leaduri de cumpărători după prima închiriere.',
      color: 'text-blue-700',
      bgGradient: 'from-blue-200 to-blue-300',
      progress: 35
    };
  }
  return {
    name: 'Rental Closer',
    emoji: '🏠',
    description: 'Gestionezi primele tranzacții de închiriere.',
    color: 'text-blue-600',
    bgGradient: 'from-blue-100 to-blue-200',
    progress: 20
  };
}
export function CareerCalculator() {
  const [rentals, setRentals] = useState<string>('0');
  const [sales, setSales] = useState<string>('0');
  const [showResult, setShowResult] = useState(false);
  const handleCalculate = () => {
    setShowResult(true);
  };
  const rentalsNum = parseInt(rentals) || 0;
  const salesNum = parseInt(sales) || 0;
  const level = getCareerLevel(rentalsNum, salesNum);
  return <div className="space-y-6" data-id="element-435">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl p-8 shadow-xl border-2 border-[#357BEB]" data-id="element-436">
        <div className="flex items-center space-x-3 mb-8" data-id="element-437">
          <CalculatorIcon className="w-8 h-8 text-[#357BEB]" data-id="element-438" />
          <h3 className="text-2xl font-bold text-[#132C53]" data-id="element-439">
            Calculator de nivel de carieră
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-440">
          <div data-id="element-441">
            <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-442">
              Număr tranzacții închiriere
            </label>
            <input type="number" value={rentals} onChange={e => setRentals(e.target.value)} className="w-full text-3xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#357BEB] transition-colors" placeholder="0" min="0" data-id="element-443" />
          </div>
          <div data-id="element-444">
            <label className="block text-sm font-semibold mb-3 text-[#132C53]" data-id="element-445">
              Număr tranzacții vânzare
            </label>
            <input type="number" value={sales} onChange={e => setSales(e.target.value)} className="w-full text-3xl font-bold text-center text-[#132C53] bg-gray-50 border-2 border-gray-200 rounded-xl p-4 focus:outline-none focus:border-[#357BEB] transition-colors" placeholder="0" min="0" data-id="element-446" />
          </div>
        </div>
        <motion.button whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} onClick={handleCalculate} className="w-full bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white px-6 py-4 rounded-2xl font-bold hover:shadow-xl transition-shadow" data-id="element-447">
          Calculează nivelul meu
        </motion.button>
      </motion.div>
      <AnimatePresence data-id="element-448">
        {showResult && <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.9
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} className="bg-white rounded-2xl overflow-hidden shadow-xl border-2 border-[#357BEB]" data-id="element-449">
            <div className="bg-gradient-to-r from-[#357BEB] to-[#21A366] text-white p-6" data-id="element-450">
              <div className="flex items-center space-x-2 mb-2" data-id="element-451">
                <AwardIcon className="w-6 h-6" data-id="element-452" />
                <h4 className="text-xl font-bold" data-id="element-453">Rezultatul calculului</h4>
              </div>
              <p className="text-gray-100" data-id="element-454">
                Închirieri: {rentalsNum} | Vânzări: {salesNum}
              </p>
            </div>
            <div className="p-6 space-y-6" data-id="element-455">
              <motion.div initial={{
            scale: 0.8,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 0.1
          }} className={`bg-gradient-to-r ${level.bgGradient} rounded-2xl p-8 border-2 border-gray-200 shadow-lg text-center`} data-id="element-456">
                <div className="text-6xl mb-4" data-id="element-457">{level.emoji}</div>
                <h3 className={`text-3xl font-bold mb-2 ${level.color}`} data-id="element-458">
                  {level.name}
                </h3>
                <div className="flex items-center justify-center space-x-2 mb-4" data-id="element-459">
                  <TrendingUpIcon className={`w-6 h-6 ${level.color}`} data-id="element-460" />
                  <span className={`text-2xl font-bold ${level.color}`} data-id="element-461">
                    Nivel {level.progress}%
                  </span>
                </div>
                <p className="text-gray-700 text-lg font-medium" data-id="element-462">
                  {level.description}
                </p>
              </motion.div>
              <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-463">
                <div className="bg-gray-100 rounded-full h-4 overflow-hidden" data-id="element-464">
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${level.progress}%`
              }} transition={{
                duration: 1,
                delay: 0.5
              }} className="bg-gradient-to-r from-[#357BEB] to-[#21A366] h-full rounded-full" data-id="element-465" />
                </div>
                <p className="text-center text-sm text-gray-600 mt-2" data-id="element-466">
                  Progres către Selling Master
                </p>
              </motion.div>
              {level.name !== 'Selling Master' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-4 text-center" data-id="element-467">
                  <p className="text-sm text-[#132C53] font-medium" data-id="element-468">
                    💡 Ești pe drumul spre Master! Continuă să performezi pentru
                    a debloca următorul nivel.
                  </p>
                </motion.div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}